import React, { useState, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';

import { CompanyForm, FinancialForm, IntegrationsForm } from '../../../components/company';
import Alert from '../../../components/shared/Alert';
import Tabs from '../../../components/shared/Tabs';

import { updateTenant, getStorageData, setUnleashedSecretKeys } from '../../../actions/tenantActions';

import { integrationServices } from '../../../constants/enums';

const tabs = [
  { label: 'General' },
  { label: 'Financial' },
  { label: 'Integrations' }
];

const CompanyPage = (props) => {
  const {
    tenant,
    updateTenant,
    getStorageData
  } = props;

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [storage, setStorage] = useState({});
  const [storageLoading, setStorageLoading] = useState(false);

  const onSubmit = async (formObj, files) => {
    setLoading(true);
    const tenantObj = {
      businessName: formObj.businessName,
      country: formObj.country,
      address1: formObj.address1,
      address2: formObj.address2,
      city: formObj.city,
      state: formObj.state,
      postCode: formObj.postCode,
      colorPrimary: formObj.colorPrimary,
      maxEmployee: formObj.maxEmployee,
      externalUsersAllowed: !!formObj.externalUsersAllowed,
      timeZone: formObj.timeZone,
      businessNumber: formObj.businessNumber || '',
      phone: formObj.phone || ''
    };

    try {
      await updateTenant(tenantObj, files);
      setLoading(false);
      setSuccessMessage('Company has been successfully updated');
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const onFinancialSubmit = async (formObj) => {
    setLoading(true);
    const financialDetails = {
      billingAccountName: formObj.billingAccountName || '',
      bankName: formObj.bankName || '',
      branchDetails: formObj.branchDetails || '',
      routingCode: formObj.routingCode || '',
      accountNumber: formObj.accountNumber || '',
      defaultPaymentTerms: formObj.defaultPaymentTerms ? Number(formObj.defaultPaymentTerms) : 0,
      defaultSalesTax: formObj.defaultSalesTax ? Number(formObj.defaultSalesTax) : 0,
      currency: formObj.currency || '',
      proformaContact: formObj.proformaContact || '',
      proformaEmail: formObj.proformaEmail || '',
      showPaymentDetails: formObj.showPaymentDetails || false,
    };

    try {
      await updateTenant({ financialDetails });
      setLoading(false);
      setSuccessMessage('Financial details have been successfully updated');
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const onIntegrationsSubmit = async (formObj) => {
    setLoading(true);
    const integrationDetails = {
      enabled: true,
      service: formObj.service || '',
    };

    try {
      if (formObj.service === integrationServices.UNLEASHED.value) {
        await dispatch(setUnleashedSecretKeys(formObj.apiId, formObj.apiKey));
      }

      await updateTenant({ integrationDetails });
      setLoading(false);
      setSuccessMessage('Integration details have been successfully updated');
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const onGetStorageData = async () => {
    try {
      setStorageLoading(true);
      const res = await getStorageData();
      if (res && res.data) {
        setStorage(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setStorageLoading(false);
    }
  };

  const initialValues = useMemo(() => {
    return {
      ...tenant,
      logoName: tenant.logo ? tenant.logo.fileName : '',
      iconName: tenant.icon ? tenant.icon.fileName : '',
      documentLogo: tenant.documentLogo ? tenant.documentLogo.fileName : '',
    };
  }, [tenant]);

  const renderForm = () => {
    switch (activeTab) {
      case 0:
        return (
          <CompanyForm
            initialValues={initialValues}
            loading={loading}
            onSubmit={onSubmit}
            storage={storage}
            storageLoading={storageLoading}
            onGetStorageData={onGetStorageData}
          />
        );
      case 1:
        return (
          <FinancialForm
            initialValues={tenant?.financialDetails}
            loading={loading}
            onSubmit={onFinancialSubmit}
          />
        );
      case 2:
        return (
          <IntegrationsForm
            initialValues={tenant?.integrationDetails}
            loading={loading}
            onSubmit={onIntegrationsSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className='settings-cmp__main company-page__container'>
      <span className='settings-title'>Company</span>

      <div className="form__container" style={{ paddingTop: 8 }}>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        >
          <div className='m-t-md'>
            {renderForm()}
          </div>
        </Tabs>
      </div>

      <Alert
        variant='success'
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    tenant: state.tenant.currentTenant,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateTenant: (tenantData, files) => dispatch(updateTenant(tenantData, files)),
    getStorageData: () => dispatch(getStorageData()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyPage);
